/**
 * What a reason code means, in words.
 *
 * The backend sends a stable code and a per-row Detail; the wording for the
 * class of rows is ours. The distinction matters because these are the only
 * sentences a person gets when their file did not do what they expected, and
 * a heading that reads "RequiredMissing: 34 rows" tells them nothing they did
 * not already know from looking at the screen.
 *
 * So each sentence answers the question actually being asked - why did THIS
 * KIND of row not go in, and what would change it - rather than restating the
 * code in spaced-out capitals.
 *
 * Adding a code to plan/import-plan-contract-zh.md section 1.4 means adding it
 * here too; reason-copy.spec.ts holds the contract's list and fails otherwise.
 */

export interface ReasonCopy {
  /** The group heading. Short enough to sit next to a count. */
  title: string;
  /** One sentence: what happened to this class of rows, and what to do. */
  sentence: string;
  /**
   * Codes this one says more precisely. A row can carry both the general code
   * and the specific one - Notes is a list so that it can - and printing both
   * sentences would say the same thing twice, the second time vaguely.
   */
  supersedes?: readonly string[];
  /**
   * The plan said this row would go in, and then it did not. A different kind
   * of thing from a row refused up front: those are "we told you in advance",
   * this is "we said we would and we did not". People act on them differently
   * - one is a file to correct, the other is a thing to retry - so the screen
   * keeps them apart, and it decides which is which from here rather than by
   * knowing one code's name.
   */
  promised?: boolean;
}

const COPY: Readonly<Record<string, ReasonCopy>> = {
  DuplicateInFile: {
    title: 'Repeated in this file',
    sentence: 'An earlier row in this same file names the same business at the'
      + ' same address. The first was used and these were left out, so one'
      + ' business does not become several records.',
  },
  AlreadyExists: {
    title: 'Already here',
    sentence: 'We already hold this business at this address. The record we'
      + ' have was left exactly as it was.',
  },
  NoIdentity: {
    title: 'Nothing to identify it by',
    sentence: 'A row is matched by its business name together with its'
      + ' address, and this one has too little of either to say which business'
      + ' it is meant to be.',
  },
  UnknownValue: {
    title: 'A value we do not recognise',
    sentence: 'A field here holds something that is not on the list of values'
      + ' we keep for it. Add the value on our side first, or correct it in'
      + ' the file.',
  },
  RequiredMissing: {
    title: 'A required field is empty',
    sentence: 'A field that every record has to carry was left blank.',
  },
  TooLong: {
    title: 'Longer than the field holds',
    sentence: 'A value here is longer than the column it would be written'
      + ' into. Shortening it in the file is the only fix.',
  },
  BadFormat: {
    title: 'Could not be read',
    sentence: 'A date, a number or a coordinate in this row is not written in'
      + ' a form we can read.',
  },
  NoChange: {
    title: 'Nothing to change',
    sentence: 'Every column we are able to import already matches what we'
      + ' hold, so there was nothing here to write.',
  },
  IgnoredEdit: {
    title: 'Edits we cannot take',
    sentence: 'This row was edited in a column that cannot be imported. The'
      + ' rest of the row was taken; that change was not.',
  },
  SameNameDifferentAddress: {
    title: 'Same name, another address',
    sentence: 'We hold a record under this name at a different address. This'
      + ' row is added as a separate record, which is right for a second'
      + ' location and wrong if this one moved - in which case you will be'
      + ' left holding both.',
  },
  ComputedColumn: {
    title: 'Worked out on our side',
    sentence: 'These values are calculated by the system - they are exported so'
      + ' the file is complete, but never read back from one. An import cannot'
      + ' set them, and the next recalculation would overwrite it if it could.',
    // Without this, the row reads as "edits we cannot take", which sounds like
    // a formatting mistake worth another attempt - so people try again, and
    // again, at something that was never going to work.
    supersedes: ['IgnoredEdit'],
  },
  WriteFailed: {
    title: 'We could not write these',
    sentence: 'These rows passed every check and then the write itself failed,'
      + ' so this is on us rather than on your file. Each one carries what went'
      + ' wrong. Importing the same file again retries them and leaves the rows'
      + ' that did go in alone.',
    promised: true,
  },
  ChangedSince: {
    title: 'Changed since the import',
    sentence: 'Somebody edited this record after the import ran. It was left'
      + ' as it now stands rather than overwritten with the older values.',
  },
  SameAddressDifferentName: {
    title: 'Same address, different name',
    sentence: 'We hold a record at this address under a different name. This'
      + ' row is added beside it, not merged into it - so if the name was'
      + ' meant to be a correction, you will end up with both.',
  },
};

/**
 * A code with no wording yet still has to render. It shows the code, and
 * points at the row's own Detail, which the backend always sends - better
 * than an empty heading, and it says plainly that this is a gap rather than
 * dressing the code up as prose.
 */
export function reasonCopy(code: string): ReasonCopy {
  return COPY[code] ?? {
    title: code,
    sentence: 'There is no wording for this reason yet. The note on each row'
      + ' below says what happened to it.',
  };
}

export function isKnownReason(code: string): boolean {
  return code in COPY;
}

/**
 * Whether this row was promised and then not delivered, as against refused
 * before anything was attempted. Unknown codes are treated as refusals: the
 * plan screen has already shown those rows, so grouping a code we have never
 * heard of under "we broke our word" would be the louder of the two guesses.
 */
export function wasPromised(code: string | null): boolean {
  return code !== null && (COPY[code]?.promised ?? false);
}

/**
 * The notes worth printing, with any that a more precise note already covers
 * left out. Order is kept, so the row still reads in the order it was told.
 */
export function notesToShow(notes: readonly string[]): string[] {
  const covered = new Set(
    notes.flatMap(code => COPY[code]?.supersedes ?? []));
  return notes.filter(code => !covered.has(code));
}
